import { createInertiaApp } from '@inertiajs/vue3';
import { createApp, h, type DefineComponent } from 'vue';
import * as Sentry from '@sentry/vue';
import { initPostHog, usePostHog } from '@/composables/usePostHog';

const pages = import.meta.glob<DefineComponent>('./pages/**/*.vue', {
    import: 'default',
});

void createInertiaApp({
    title: (title) => (title ? `${title} · Pokor` : 'Pokor Status'),
    resolve: (name) => {
        const page = pages[`./pages/${name}.vue`];

        if (!page) {
            throw new Error(`Inertia page not found: ${name}`);
        }

        return page();
    },
    progress: {
        color: '#605dff',
    },
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) }).use(plugin);

        const tracesSampleRate = Number(import.meta.env.VITE_SENTRY_TRACES_SAMPLE_RATE);

        Sentry.init({
            app,
            dsn: import.meta.env.VITE_SENTRY_DSN ?? '',
            environment: import.meta.env.VITE_SENTRY_ENVIRONMENT ?? import.meta.env.MODE,
            release: String(props.initialPage.props.release ?? 'dev'),
            integrations: [Sentry.browserTracingIntegration()],
            tracesSampleRate: Number.isFinite(tracesSampleRate) ? tracesSampleRate : 0,
            // Restrict distributed-tracing headers to our own domain only, so we
            // don't inject sentry-trace/baggage headers into third-party requests.
            tracePropagationTargets: ['localhost', /^https:\/\/status\.pokor\.dev/],
        });

        initPostHog();
        const ph = usePostHog();

        app.config.errorHandler = (err) => {
            Sentry.captureException(err);
            ph.captureException(err);
        };

        app.mount(el);
    },
});
